import React, { useState, useEffect } from 'react';
import { useMutation } from '@apollo/client';
import { Modal, ModalTitle, ModalContent, ModalFooter, TextInput, ComboBox, ComboBoxOption, Checkbox } from '@utahdts/utah-design-system';
import { Row, Col } from 'react-bootstrap';
import DwriSystemHeader from './sections/DwriSystemHeader';
import DwriManagementInfo from './sections/DwriManagementInfo';
import DwriOperationalDays from './sections/DwriOperationalDays';
import DwriServiceConnections from './sections/DwriServiceConnections';
import DwriAverageAnnualDemandPerErc from './sections/DwriAverageAnnualDemandPerErc';
import DwriPeakDayDemandPerErc from './sections/DwriPeakDayDemandPerErc';
import ButtonSubmit from '../../../reuseableComponents/buttons/ButtonSubmit';
import ButtonProcessing from '../../../reuseableComponents/buttons/ButtonProcessing';
import FakeLink from '../../../reuseableComponents/FakeLink';
import { UPDATE_WATER_USE_SYSTEM, GET_WATER_USE_WATER_SYSTEM } from '../../../../queries/engineeringQrys';
import useDwriCalculationsData from '../../../../hooks/engineering/water_use/useDwriCalculationsData';

const getStringValue = (value) => {
    if (typeof value === 'string') {
        return value;
    }

    if (typeof value === 'number') {
        return String(value);
    }

    if (value && typeof value === 'object') {
        if (typeof value.target?.value === 'string') {
            return value.target.value;
        }

        if (typeof value.value === 'string') {
            return value.value;
        }

        if (typeof value.label === 'string') {
            return value.label;
        }
    }

    return '';
};

const toNullableTrimmedString = (value) => {
    const normalizedValue = getStringValue(value).trim();
    return normalizedValue || null;
};

const DwriCalculationsTab = ({ pwsId, systemName, systemInfo, assignableEngineers = [], loading = {}, errors = {}, years = [] }) => {
    const user = localStorage.getItem("user")
        ? JSON.parse(localStorage.getItem("user"))
        : { isPublic: true };

    const canEditWaterSystemInfo = user?.permissions?.includes("edit_water_use_system");

    const { averageAnnualDemandRows } = useDwriCalculationsData({ pwsId, years });

    const [showEditModal, setShowEditModal] = useState(false);
    const [cca, setCca] = useState(false);
    const [pwsEngineerReport, setPwsEngineerReport] = useState(false);
    const [assignedTo, setAssignedTo] = useState('');
    const [pwsFileNumber, setPwsFileNumber] = useState('');
    const [d2Link, setD2Link] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [saveError, setSaveError] = useState(null);
    const [saveSuccess, setSaveSuccess] = useState(false);

    const [saveSystemInfo] = useMutation(UPDATE_WATER_USE_SYSTEM, {
        refetchQueries: [{ query: GET_WATER_USE_WATER_SYSTEM, variables: { pwsId } }],
        awaitRefetchQueries: true,
    });

    useEffect(() => {
        if (systemInfo) {
            setCca(!!systemInfo.cca);
            setPwsEngineerReport(!!systemInfo.engReport);
            setAssignedTo(
                systemInfo.assignedUser
                    ? [systemInfo.assignedUser.lastName, systemInfo.assignedUser.firstName].filter(Boolean).join(', ')
                    : ''
            );
            setPwsFileNumber(systemInfo.fileNumber || '');
            setD2Link(systemInfo.d2Link || '');
        }
    }, [systemInfo]);

    const buildPayload = () => {
        const normalizedAssignedTo = getStringValue(assignedTo);
        const matched = assignableEngineers.find((eng) => {
            const firstName = eng?.firstName?.trim() || '';
            const lastName = eng?.lastName?.trim() || '';
            return [lastName, firstName].filter(Boolean).join(', ') === normalizedAssignedTo;
        });

        return {
            pwsId,
            assignedUserUmdId: matched?.umdId ?? null,
            cca,
            engReport: pwsEngineerReport,
            fileNumber: pwsFileNumber !== '' && pwsFileNumber != null ? Number.parseInt(pwsFileNumber, 10) : null,
            d2Link: toNullableTrimmedString(d2Link),
        };
    };

    const handleSave = async () => {
        setSaveError(null);
        setSaveSuccess(false);
        setIsSubmitting(true);
        try {
            await saveSystemInfo({ variables: { UpdateWaterUseSystemInput: buildPayload() } });
            setSaveSuccess(true);
            setTimeout(() => {
                setSaveSuccess(false);
                setShowEditModal(false);
            }, 2000);
        } catch (err) {
            setSaveError(err.message || 'Save failed. Please try again.');
        } finally {
            setIsSubmitting(false);
        }
    };

    const assignedToOptions = assignableEngineers
        .map((engineer) => {
            const firstName = engineer?.firstName?.trim() || '';
            const lastName = engineer?.lastName?.trim() || '';
            const fullName = [lastName, firstName].filter(Boolean).join(', ');

            return {
                key: engineer?.umdId || fullName,
                value: fullName,
            };
        })
        .filter((option) => option.value);

    return (
        <div className="utah-design-system">
            <DwriSystemHeader
                systemName={systemName}
                loading={loading.info}
            />
            {canEditWaterSystemInfo && (
                <FakeLink onClick={() => setShowEditModal(true)}>Edit</FakeLink>
            )}
            <DwriManagementInfo
                systemInfo={systemInfo}
                loading={loading.info}
                error={errors.info}
            />
            {showEditModal && (
                <Modal
                    className="modal--medium"
                    onClose={() => setShowEditModal(false)}
                    onEscape={() => setShowEditModal(false)}
                >
                    <ModalTitle>Manage Water System Information</ModalTitle>
                    {saveError && (
                        <div className="alert alert-danger mt-2 mx-3" role="alert">{saveError}</div>
                    )}
                    {saveSuccess && (
                        <div className="alert alert-success mt-2 mx-3" role="alert">Saved successfully.</div>
                    )}
                    <ModalContent id="manage-water-system-modal-content">
                        <Row className="mb-3">
                            <Col xs={4}>
                                <ComboBox
                                    id="assignedTo"
                                    label="Assigned To"
                                    name="assignedTo"
                                    autoComplete="off"
                                    value={getStringValue(assignedTo)}
                                    onChange={(value) => setAssignedTo(getStringValue(value))}
                                >
                                    {assignedToOptions.map((engineer) => (
                                        <ComboBoxOption
                                            key={engineer.key}
                                            label={engineer.value}
                                            value={engineer.value}
                                        />
                                    ))}
                                </ComboBox>
                            </Col>
                            <Col xs={4}>
                                <Checkbox
                                    id="cca"
                                    label="CCA"
                                    name="cca"
                                    value={cca}
                                    onChange={(e) => setCca(e.target.checked)}
                                    wrapperClassName="checkbox-label-top"
                                />
                            </Col>
                            <Col xs={4}>
                                <Checkbox
                                    id="pwsEngineerReport"
                                    label="PWS Engineer Report"
                                    name="pwsEngineerReport"
                                    value={pwsEngineerReport}
                                    onChange={(e) => setPwsEngineerReport(e.target.checked)}
                                    wrapperClassName="checkbox-label-top"
                                />
                            </Col>
                        </Row>

                        <Row className="mb-3">
                            <Col>
                                <TextInput
                                    id="pwsFileNumber"
                                    label="PWS File Number"
                                    name="pwsFileNumber"
                                    value={pwsFileNumber}
                                    onChange={(e) => setPwsFileNumber(e.target.value)}
                                />
                            </Col>
                        </Row>
                        <Row className="mb-3">
                            <Col>
                                <TextInput
                                    id="d2Link"
                                    label="D2 Link"
                                    name="d2Link"
                                    value={d2Link}
                                    onChange={(e) => setD2Link(e.target.value)}
                                />
                            </Col>
                        </Row>
                    </ModalContent>
                    <ModalFooter id="manage-water-system-modal-footer">
                        <div className="d-flex justify-content-end">
                            {isSubmitting
                                ? <ButtonProcessing id="ddw-primary" label="Saving" />
                                : <ButtonSubmit id="ddw-primary" label="Save" onClick={handleSave} />
                            }
                        </div>
                    </ModalFooter>
                </Modal>
            )}
            <br/>
            <DwriOperationalDays 
                pwsId={pwsId}
                years={years}
            />
            <br/>
            <DwriServiceConnections 
                pwsId={pwsId}
                years={years}
            />
            <br/>
            <DwriPeakDayDemandPerErc 
                pwsId={pwsId}
                years={years}
            />
            <br/>
            <DwriAverageAnnualDemandPerErc
                years={years}
                rows={averageAnnualDemandRows}
            />
        </div>
    );
};

export default DwriCalculationsTab;